import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, MessageCircle, Sparkles } from 'lucide-react';

// Floating stars background
const FloatingStars = () => {
  const stars = useMemo(() =>
    [...Array(20)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 10 + 5,
      duration: Math.random() * 5 + 5,
      delay: Math.random() * 3
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`
          }}
          animate={{
            y: [-10, 10, -10],
            rotate: [0, 180, 360],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <Star
            size={star.size}
            className="text-amber-400/20"
            fill="currentColor"
          />
        </motion.div>
      ))}
    </div>
  );
};

// Testimonial card component
const TestimonialCard = ({ testimonial, index }) => {
  const gradients = [
    'from-amber-500 via-orange-500 to-rose-500',
    'from-purple-500 via-violet-500 to-indigo-500',
    'from-teal-500 via-emerald-500 to-green-500'
  ];

  const glowColors = [
    'rgba(245, 158, 11, 0.2)',
    'rgba(139, 92, 246, 0.2)',
    'rgba(20, 184, 166, 0.2)'
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateX: -10 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true }}
      transition={{
        delay: index * 0.15,
        duration: 0.6,
        type: 'spring',
        stiffness: 100
      }}
      whileHover={{
        y: -10,
        transition: { duration: 0.3 }
      }}
      className="relative group"
      style={{ perspective: 1000 }}
    >
      {/* Glow effect */}
      <motion.div
        className="absolute -inset-4 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at center, ${glowColors[index % 3]}, transparent 70%)`,
          filter: 'blur(20px)'
        }}
      />

      {/* Card */}
      <div className="relative">
        {/* Gradient border */}
        <div className={`absolute inset-0 rounded-3xl p-[1px] bg-gradient-to-br ${gradients[index % 3]}`}>
          <div className="w-full h-full bg-[#0f0f2a] rounded-3xl" />
        </div>

        {/* Content */}
        <div className="relative p-8 lg:p-10">
          {/* Quote icon */}
          <motion.div
            className={`absolute -top-6 -left-2 w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${gradients[index % 3]}`}
            whileHover={{ rotate: 10, scale: 1.1 }}
          >
            <Quote className="w-6 h-6 text-white" />
          </motion.div>

          {/* Rating stars */}
          <div className="flex gap-1 mb-6 mt-2">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 + i * 0.1 }}
              >
                <Star
                  className="w-5 h-5 text-amber-400"
                  fill="currentColor"
                />
              </motion.div>
            ))}
          </div>

          {/* Testimonial text */}
          <p className="text-gray-300 text-lg leading-relaxed mb-8 font-serif italic">
            "{testimonial.text}"
          </p>

          {/* Author info */}
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl bg-gradient-to-br ${gradients[index % 3]}`}
            >
              {testimonial.author.charAt(0)}
            </div>

            <div>
              <h4 className="font-bold text-white text-lg">
                {testimonial.author}
              </h4>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {testimonial.role}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      text: "I can't thank Carter Case enough for creating 'Ned and Nancy.' These books have become an essential part of my classroom. The kids absolutely adore the characters and the witty storytelling. It's a fantastic way to introduce humor and mystery in children's literature while promoting critical thinking.",
      author: 'JENNIFER L.',
      role: 'School Teacher'
    },
    {
      text: "Ned and Nancy are my absolute favorites! I can't get enough of their hilarious adventures. It's like they take me on a journey full of surprises every time I open one of their books. Carter Case, you're the coolest!",
      author: 'ALICIA H.',
      role: 'Parent'
    },
    {
      text: "Our book club chose 'Ned and Nancy' for our children's selection, and it was a hit! Not only did the kids love it, but the parents found themselves chuckling along as well. Carter Case's storytelling is truly magical; it's a must-read for kids and adults alike.",
      author: 'JUAN C.',
      role: 'Book Club Member'
    }
  ];

  return (
    <div className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 50%, #0a0a1a 100%)'
          }}
        />

        {/* Radial glows */}
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(245, 158, 11, 0.1) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <FloatingStars />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-amber-500" />
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Sparkles className="w-8 h-8 text-amber-400" />
            </motion.div>
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-amber-500" />
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">What Readers </span>
            <span className="gradient-text-gold">Are Saying</span>
          </h2>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-serif italic">
            Join thousands of families who have fallen in love with Ned and Nancy
          </p>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </div>

        {/* Stats section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {[
            { value: '50K+', label: 'Happy Readers' },
            { value: '8', label: 'Books Published' },
            { value: '4.9', label: 'Average Rating' },
            { value: '15+', label: 'Awards Won' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6"
            >
              <div className="font-display text-4xl lg:text-5xl font-bold gradient-text-gold mb-2">
                {stat.value}
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-widest">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Testimonials;
